/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

import { I18NError } from 'Utils';
import { formatErrorResponse } from 'responseFormatterFunctions';
import { fetchGet, fetchPost, fetchPut } from 'FetchAPIs';
import { DimensionsType } from 'EntitiesTypes';
import {
  AddDimensionsForDeviceType,
  LoadDimensionsForDeviceType,
  UpdateDimensionsForDeviceType,
} from 'HierarchyActionTypes';

const baseHierarchiesURL = '/api/hierarchies';
const datalakeDataAPI = '/api/datalake/data';

const messages = defineMessages({
  errorGettingDimensionsForDevice: 'Error getting dimensions for device. {message}',
  errorCreatingDimensionsForDevice: 'Error creating dimensions for device. {message}',
  errorUpdatingDimensionsForDevice: 'Error updating dimensions for device. {message}',
  errorGettingParentAssetsForDevice: 'Error getting parent assets for device. {message}',
  errorGettingParentLocationsForDevice: 'Error getting parent locations for device. {message}',
});

export const getDimensionsForDeviceAPI = async (
  hierarchyPath: string
): ReturnType<LoadDimensionsForDeviceType> =>
  fetchGet(`${datalakeDataAPI}${hierarchyPath}/dimensions`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingDimensionsForDevice, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that creates dimensions for a specific device
 * @param deviceTypeId - device type id
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user creating the dimensions
 * @returns
 */
export const createDimensionsForDevice = async (
  deviceTypeId: string,
  dimensions: DimensionsType,
  userEmailId: string
): ReturnType<AddDimensionsForDeviceType> =>
  fetchPost(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices/dimensions`,
    dimensions,
    {
      userEmailId,
    },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorCreatingDimensionsForDevice, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that updates dimensions for a specific device
 * @param deviceTypeId - device type id
 * @param dimensions - array of dimensions
 * @param userEmailId - email of the user creating the dimensions
 * @returns
 */
export const updateDimensionsForDevice = async (
  deviceTypeId: string,
  dimensions: DimensionsType,
  userEmailId: string
): ReturnType<UpdateDimensionsForDeviceType> =>
  fetchPut(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices/dimensions`,
    dimensions,
    {
      userEmailId,
    },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingDimensionsForDevice, {
        message: formatErrorResponse(error),
      });
    });

export const getParentAssetsForDeviceAPI = async (
  deviceTypeId: string,
  deviceId: string
): Promise<any> =>
  fetchGet(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices/${deviceId}/relations/assets`,
    null,
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingParentAssetsForDevice, {
        message: formatErrorResponse(error),
      });
    });

export const getParentLocationsForDeviceAPI = async (
  deviceTypeId: string,
  deviceId: string
): Promise<any> =>
  fetchGet(
    `${baseHierarchiesURL}/deviceTypes/${deviceTypeId}/devices/${deviceId}/relations/locations`,
    null,
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingParentLocationsForDevice, {
        message: formatErrorResponse(error),
      });
    });
